import { useEffect, useRef } from 'react';
import { AppState } from 'react-native';
import EventSource from 'react-native-sse';
import { netDebug } from '../utils/netDebug';

import { buildBackofficeOrderStreamUrl } from '../api/backofficeOrders';
import { useAppDispatch } from '../redux/helper';
import {
  removeOrderById,
  upsertOrderDoc,
} from '../redux/backoffice/ordersSlice';
import type { OrderDoc } from '../redux/backoffice/ordersSlice';
import {
  removeOrderFromCustomerSlices,
  upsertOrderInCustomerSlices,
} from '../redux/backoffice/customerOrdersSlice';
import { upsertRecentTransaction } from '../redux/backoffice/recentTransactionsSlice';

type BackofficeOrderStreamData =
  | {
      event: 'upserted';
      order: OrderDoc;
    }
  | {
      event: 'removed';
      orderId: string;
    };

type BackofficeOrderStreamEnvelope = {
  status: number;
  data?: BackofficeOrderStreamData;
  message?: string;
};

type UseBackofficeOrderSyncStreamArgs = {
  enabled: boolean;
  baseUrl: string;
  accessToken: string;
  locationId: string;
  startISO?: string;
  endISO?: string;
  onReconcile: () => void | Promise<void>;
  onTokenExpired?: () => void;
};

const parseStreamEnvelope = (
  event: any,
): BackofficeOrderStreamEnvelope | null => {
  try {
    return typeof event?.data === 'string'
      ? JSON.parse(event.data)
      : event?.data || null;
  } catch {
    return null;
  }
};

export const useBackofficeOrderSyncStream = ({
  enabled,
  baseUrl,
  accessToken,
  locationId,
  startISO,
  endISO,
  onReconcile,
  onTokenExpired,
}: UseBackofficeOrderSyncStreamArgs) => {
  const dispatch = useAppDispatch();
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryCountRef = useRef(0);
  const reconcileRef = useRef(onReconcile);
  const appIsActiveRef = useRef(true);
  const onTokenExpiredRef = useRef(onTokenExpired);

  reconcileRef.current = onReconcile;
  onTokenExpiredRef.current = onTokenExpired;

  useEffect(() => {
    if (!enabled || !baseUrl || !accessToken || !locationId) {
      return;
    }

    let closedByCleanup = false;
    let authErrorSeen = false;

    const clearReconnectTimer = () => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    };

    const closeStream = () => {
      try {
        if (eventSourceRef.current) netDebug.sseClose('bo-order');
        eventSourceRef.current?.close();
      } catch {}
      eventSourceRef.current = null;
    };

    const runReconcile = () => {
      void reconcileRef.current();
    };

    const scheduleReconnect = (connect: () => void) => {
      if (closedByCleanup || !appIsActiveRef.current) return;

      clearReconnectTimer();
      const delay = Math.min(30000, 1000 * 2 ** retryCountRef.current);
      retryCountRef.current += 1;

      reconnectTimerRef.current = setTimeout(() => {
        connect();
      }, delay);
    };

    const connect = () => {
      if (closedByCleanup || !appIsActiveRef.current) return;

      clearReconnectTimer();
      closeStream();

      const url = buildBackofficeOrderStreamUrl(
        baseUrl,
        accessToken,
        locationId,
        startISO,
        endISO,
      );
      const headers = {
        Authorization: `Bearer ${accessToken}`,
        'X-Location-ID': locationId,
      };

      const es = new EventSource(url, { headers });
      netDebug.sseOpen('bo-order');
      const esAny = es as any;

      const handleConnected = (event: any) => {
        // See useProductSyncStream: queued events from a dying socket must
        // not write into the new-location caches.
        if (closedByCleanup) return;
        const envelope = parseStreamEnvelope(event);
        if (envelope?.status !== 200) return;

        retryCountRef.current = 0;
        authErrorSeen = false;
        runReconcile();
      };

      const handleOrderUpsert = (event: any) => {
        if (closedByCleanup) return;
        const envelope = parseStreamEnvelope(event);
        if (envelope?.status !== 200 || !envelope.data) return;
        if (envelope.data.event !== 'upserted') return;

        const order = envelope.data.order;
        if (order?.bp_order_id) {
          dispatch(upsertOrderDoc(order));
          dispatch(upsertRecentTransaction(order));
          // Fan out to any customer-details screen state that already has
          // this order loaded (or matches its bp_order_customer_id). No-op
          // when no customer entry references it.
          dispatch(upsertOrderInCustomerSlices(order));
        }
      };

      const handleOrderRemoved = (event: any) => {
        if (closedByCleanup) return;
        const envelope = parseStreamEnvelope(event);
        if (envelope?.status !== 200 || !envelope.data) return;
        if (envelope.data.event !== 'removed') return;
        const orderId = String(envelope.data.orderId || '').trim();
        if (orderId) {
          dispatch(removeOrderById(orderId));
          dispatch(removeOrderFromCustomerSlices(orderId));
        } else {
          runReconcile();
        }
      };

      const handleError = (event?: any) => {
        const envelope = parseStreamEnvelope(event);
        const isAuthError = envelope?.status === 401 || envelope?.status === 403;

        if (isAuthError && !authErrorSeen) {
          authErrorSeen = true;
          console.warn('Backoffice order stream: auth error, waiting for token refresh');
          closeStream();
          onTokenExpiredRef.current?.();
          reconnectTimerRef.current = setTimeout(() => {
            if (closedByCleanup || !appIsActiveRef.current) return;
            authErrorSeen = false;
            connect();
          }, 30000);
          return;
        }

        if (envelope?.status && envelope.status !== 200) {
          console.warn('Backoffice order stream error:', envelope.message);
        }

        closeStream();
        if (!authErrorSeen) {
          scheduleReconnect(connect);
        }
      };

      esAny.addEventListener('connected', handleConnected);
      esAny.addEventListener('order:upserted', handleOrderUpsert);
      esAny.addEventListener('order:removed', handleOrderRemoved);
      esAny.addEventListener('ping', () => {});
      es.addEventListener('error', handleError);

      eventSourceRef.current = es;
    };

    appIsActiveRef.current = true;
    connect();

    const appStateSubscription = AppState.addEventListener(
      'change',
      nextState => {
        if (nextState === 'active') {
          appIsActiveRef.current = true;
          connect();
          return;
        }

        if (nextState === 'background' || nextState === 'inactive') {
          appIsActiveRef.current = false;
          clearReconnectTimer();
          closeStream();
        }
      },
    );

    return () => {
      closedByCleanup = true;
      clearReconnectTimer();
      appStateSubscription.remove();
      closeStream();
    };
  }, [
    enabled,
    baseUrl,
    accessToken,
    locationId,
    startISO,
    endISO,
    dispatch,
  ]);
};
